import type { ReactNode } from 'react';
import {
  RobotOutlined,
  ApiOutlined,
  FilePdfOutlined,
  BellOutlined,
  CheckSquareOutlined,
  DatabaseOutlined,
  FileSyncOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  AppstoreOutlined,
  BranchesOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import { colors } from '@/theme/tokens';

// Visual treatment for each executor (`WorkflowNode.classId`). Used by both
// the list-card chain preview and the detail pipeline viz so a node's role
// is recognizable at a glance no matter where it appears.
//
// Tones intentionally encode SEMANTICS, not just variety:
//   • ink/violet → AI/agent reasoning (the workflow's "thinking" steps)
//   • green     → successful side-effect on internal state (writes, todos)
//   • amber     → reaches outside the system (notifications, PDFs, files)
//   • red       → destructive (delete)
//   • slate     → fallback for unknown / generic executors

export type ExecutorTone = 'ink' | 'violet' | 'green' | 'amber' | 'red' | 'slate';

export interface ExecutorMeta {
  label: string;
  description: string;
  icon: ReactNode;
  tone: ExecutorTone;
}

const META: Record<string, ExecutorMeta> = {
  EXECUTE_AI_PROMPT: {
    label: 'AI prompt',
    description: 'Runs an LLM completion with the configured prompt and plugins',
    icon: <CodeOutlined />,
    tone: 'ink',
  },
  AGENT_NODE_EXECUTOR: {
    label: 'Agent turn',
    description: 'Hands the step to a named agent with its own instructions and tools',
    icon: <RobotOutlined />,
    tone: 'violet',
  },
  AI_GENERATE_PDF: {
    label: 'Generate PDF',
    description: 'Renders an AI-authored PDF report from prior step output',
    icon: <FilePdfOutlined />,
    tone: 'amber',
  },
  AgentToPDFExecutor: {
    label: 'Agent → PDF',
    description: 'An agent run whose output is converted to a PDF artifact',
    icon: <FilePdfOutlined />,
    tone: 'amber',
  },
  NOTIFY_CARE_TEAM: {
    label: 'Notify care team',
    description: 'Sends a notification to the assigned care team',
    icon: <BellOutlined />,
    tone: 'amber',
  },
  CREATE_EMPLOYEE_TODO: {
    label: 'Create todo',
    description: 'Creates an employee follow-up task',
    icon: <CheckSquareOutlined />,
    tone: 'green',
  },
  STORE_SQL_OBJ: {
    label: 'Store (SQL)',
    description: 'Persists a record to SQL Server',
    icon: <DatabaseOutlined />,
    tone: 'green',
  },
  STORE_COSMOS_OBJ: {
    label: 'Store (Cosmos)',
    description: 'Persists a record to Cosmos DB',
    icon: <DatabaseOutlined />,
    tone: 'green',
  },
  STORE_TEMPLATE_NOTES: {
    label: 'Store note',
    description: 'Persists a generated note to the member chart',
    icon: <FileTextOutlined />,
    tone: 'green',
  },
  STORE_MEMBER_FOOD: {
    label: 'Log food',
    description: 'Records a food consumption entry for the member',
    icon: <DatabaseOutlined />,
    tone: 'green',
  },
  STORE_MEMBER_CARE_GAP: {
    label: 'Store care gap',
    description: 'Records a care gap finding (HEDIS quality measure)',
    icon: <CheckSquareOutlined />,
    tone: 'green',
  },
  UPDATE_PROPERTY_SQL_OBJ: {
    label: 'Update SQL field',
    description: 'Patches a single property on an existing SQL row',
    icon: <EditOutlined />,
    tone: 'green',
  },
  UPDATE_PROPERTY_COSMOS_OBJ: {
    label: 'Update Cosmos field',
    description: 'Patches a single property on an existing Cosmos doc',
    icon: <EditOutlined />,
    tone: 'green',
  },
  REMOVE_DUPLICATE_SQL: {
    label: 'Dedupe rows',
    description: 'Removes duplicate SQL rows by key',
    icon: <DeleteOutlined />,
    tone: 'red',
  },
  GENERATE_RECOMMENDATION: {
    label: 'Recommendation',
    description: 'Generates a clinical recommendation from prior step output',
    icon: <ThunderboltOutlined />,
    tone: 'ink',
  },
  SUMMARY: {
    label: 'Summarize',
    description: 'Aggregates fan-out branches into a single summary output',
    icon: <FileSyncOutlined />,
    tone: 'ink',
  },
  CONDITIONAL_ROUTER: {
    label: 'Branch',
    description: 'Routes to one of several next nodes based on a condition',
    icon: <BranchesOutlined />,
    tone: 'violet',
  },
};

export function getExecutorMeta(classId: string | undefined | null): ExecutorMeta {
  if (!classId) return UNKNOWN;
  return (
    META[classId] ?? {
      label: classId,
      description: 'Unrecognized executor — see the workflow JSON for raw arguments',
      icon: <AppstoreOutlined />,
      tone: 'slate',
    }
  );
}

const UNKNOWN: ExecutorMeta = {
  label: 'Step',
  description: 'No executor configured',
  icon: <ApiOutlined />,
  tone: 'slate',
};

// Tone → background / border / foreground triplet, theme-aware. Used by
// both the chain pill and the pipeline node card.
export function executorPalette(tone: ExecutorTone, isDark: boolean): {
  bg: string; border: string; fg: string; iconBg: string;
} {
  const map: Record<ExecutorTone, { bg: string; border: string; fg: string; iconBg: string }> =
    isDark
      ? {
          ink: {
            bg: colors.ink[700] + '33',
            border: colors.ink[700] + '88',
            fg: colors.ink[100],
            iconBg: colors.ink[700] + '55',
          },
          violet: {
            bg: colors.violet[700] + '33',
            border: colors.violet[700] + '88',
            fg: colors.violet[100],
            iconBg: colors.violet[700] + '55',
          },
          green: {
            bg: colors.green[700] + '33',
            border: colors.green[700] + '88',
            fg: colors.green[100],
            iconBg: colors.green[700] + '55',
          },
          amber: {
            bg: colors.amber[700] + '33',
            border: colors.amber[700] + '88',
            fg: colors.amber[100],
            iconBg: colors.amber[700] + '55',
          },
          red: {
            bg: colors.red[700] + '33',
            border: colors.red[700] + '88',
            fg: colors.red[100],
            iconBg: colors.red[700] + '55',
          },
          slate: {
            bg: colors.slate[800],
            border: colors.slate[700],
            fg: colors.slate[200],
            iconBg: colors.slate[700],
          },
        }
      : {
          ink: { bg: colors.ink[50], border: colors.ink[200], fg: colors.ink[700], iconBg: colors.ink[100] },
          violet: { bg: colors.violet[50], border: colors.violet[200], fg: colors.violet[700], iconBg: colors.violet[100] },
          green: { bg: colors.green[100], border: colors.green[100], fg: colors.green[700], iconBg: '#A7F3D0' },
          amber: { bg: colors.amber[100], border: colors.amber[100], fg: colors.amber[700], iconBg: '#FDE68A' },
          red: { bg: colors.red[100], border: colors.red[100], fg: colors.red[700], iconBg: '#FECACA' },
          slate: { bg: colors.slate[50], border: colors.slate[200], fg: colors.slate[700], iconBg: colors.slate[100] },
        };
  return map[tone];
}
